import { AfterViewInit, Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy, input, output, viewChild } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import * as _ from 'lodash-es';
import { MatAutocompleteTrigger as MatAutocompleteTrigger, MatAutocomplete, MatOption } from '@angular/material/autocomplete';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { RxReactiveFormsModule } from '@rxweb/reactive-form-validators';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-autocomplete-filter-model',
    templateUrl: './autocomplete-filter-model.component.html',
    styleUrls: ['./autocomplete-filter-model.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatFormField, MatLabel, MatInput, MatAutocompleteTrigger, FormsModule, RxReactiveFormsModule, MatAutocomplete, MatOption, MatError, MatIcon, MatSuffix, TranslatePipe]
})
export class AutocompleteFilterModelComponent {
    readonly autoTrigger = viewChild(MatAutocompleteTrigger);

    @Input() label: string;
    readonly error = input<string>();
    readonly placeholder = input<string>();
    readonly required = input<boolean>();
    readonly name = input<string>();

    @Input() ngModel: any;
    readonly ngModelChange = output<any>();

    readonly options = input<any[]>();
    readonly filterKey = input<string>('');
    readonly displayKey = input<string>('');

    readonly alphabetical = input<boolean>(false);
    readonly order = input<'asc' | 'desc'>('asc');

    lastValue: string | undefined = undefined;
    private sub: Subscription = new Subscription();

    get accessor(): (value: any) => any {
        return _.property(this.filterKey());
    }

    get filteredOptions(): any {
        return this._filter(this.lastValue);
    };

    filteredOptions$: Subject<any[]> = new Subject<any[]>();

    displayWith = (value: any) => {
        if (value) {
            return typeof value === 'string' ? value : this.accessor(value);
        }
        else {
            console.log("ERROR : Autocomplete no value");
        }
    };

    filterChange(event: string) {
        this.lastValue = event;
    }

    filter() {
        if (this.ngModel === '')
            this.ngModel = '';
    }

    private _filter(value: string | any): string[] | any[] {
        let filterValue = '';
        if (typeof value === 'string')
            filterValue = value.toLowerCase();
        else
            filterValue = this._accessor(value)?.toLowerCase();
        const options = this.options();
        if (options) {
            return options.filter(option => {
                if (typeof option === 'string') {
                    return option !== undefined && !_.isEmpty(option) && this._compare(filterValue, option);
                } else {
                    return option !== undefined && this._accessor(option) !== undefined && !_.isEmpty(this._accessor(option)) && this._compare(filterValue, this._accessor(option));
                }
            })
                .sort((a, b) => {
                    const order = this.order() || 'asc'; // Valeur par défaut 'asc'

                    const compareStrings = (str1: string, str2: string) => {
                        if (order === 'asc') {
                            return str1.localeCompare(str2);
                        } else if (order === 'desc') {
                            return str2.localeCompare(str1); // Inverser l'ordre
                        }
                        return 0; // Par défaut
                    };

                    // Si les deux valeurs sont des chaînes de caractères
                    if (typeof a === 'string' && typeof b === 'string') {
                        a = a.toLowerCase();
                        b = b.toLowerCase();

                        const aStartsWith = a.startsWith(filterValue);
                        const bStartsWith = b.startsWith(filterValue);
                        const aIncludes = a.includes(filterValue);
                        const bIncludes = b.includes(filterValue);

                        // 1. Si a commence par filterValue mais pas b, a doit être avant
                        if (aStartsWith && !bStartsWith) {
                            return -1;
                        }
                        // 2. Si b commence par filterValue mais pas a, b doit être avant
                        else if (!aStartsWith && bStartsWith) {
                            return 1;
                        }
                        // 3. Si a et b commencent tous les deux par filterValue, les trier par ordre alphabétique
                        else if (aStartsWith && bStartsWith) {
                            return compareStrings(a, b);
                        }
                        // 4. Si aucun des deux ne commence par filterValue mais ils le contiennent, les trier alphabétiquement
                        else if (aIncludes && bIncludes) {
                            return compareStrings(a, b);
                        }
                        // 5. Si a contient filterValue mais pas b, a doit être avant
                        else if (aIncludes && !bIncludes) {
                            return -1;
                        }
                        // 6. Si b contient filterValue mais pas a, b doit être avant
                        else if (!aIncludes && bIncludes) {
                            return 1;
                        }
                        // 7. Sinon, trier par ordre alphabétique
                        else {
                            return compareStrings(a, b);
                        }
                    }
                    // Si ce ne sont pas des chaînes, utiliser un accessoire pour récupérer la chaîne à comparer
                    else {
                        const accessorA = this._accessor(a).toLowerCase();
                        const accessorB = this._accessor(b).toLowerCase();

                        const aStartsWith = accessorA.startsWith(filterValue);
                        const bStartsWith = accessorB.startsWith(filterValue);
                        const aIncludes = accessorA.includes(filterValue);
                        const bIncludes = accessorB.includes(filterValue);

                        // Même logique que ci-dessus
                        if (aStartsWith && !bStartsWith) {
                            return -1;
                        } else if (!aStartsWith && bStartsWith) {
                            return 1;
                        } else if (aStartsWith && bStartsWith) {
                            return compareStrings(accessorA, accessorB);
                        } else if (aIncludes && bIncludes) {
                            return compareStrings(accessorA, accessorB);
                        } else if (aIncludes && !bIncludes) {
                            return -1;
                        } else if (!aIncludes && bIncludes) {
                            return 1;
                        } else {
                            return compareStrings(accessorA, accessorB);
                        }
                    }
                });

        }
        else return [];
    }

    protected _accessor(option: any): string {
        const filterKey = this.filterKey();
        if (this.accessor) {
            return this.accessor(option);
        } else
            if (_.isArray(filterKey)) {
                let v = '';
                filterKey.forEach(s => v = _.join([v, _.property(s)(option)], ' '));
                return v;
            } else {
                return _.property(filterKey)(option) as string;
            }

    }

    protected _compare(filterValue: string, option: string) {
        return (!_.isEmpty(filterValue)) ? option.toLowerCase().includes(filterValue) : true;
    }

    getLabel(option: any) {
        const displayKey = this.displayKey();
        return _.isString(option) ? option : !_.isEmpty(displayKey) ? _.get(option, displayKey) : this.accessor(option);
    }

    onFocus() {
        if (!this.ngModel) {
            this.filterChange('');
        }
    }
}
